import React, { MouseEvent } from 'react';
import { CSVLink } from 'react-csv';
import ExportIcon from 'assets/Icons/export/ExportIcon';
import { BuildingProps } from 'services/hooks/building-service';
import { AggregatedUsageResponse, Aggregation, SpaceProps } from 'services/hooks/space-service';
import { colours, SpaceReportingType } from 'utils/constants';
import { formatDateToString } from 'utils/time';
import { ApplessOccupancyLogsData } from '../SpaceAnalytics/ApplessOccupancyLogsTable';
import { utcToZonedTime, format } from 'date-fns-tz';
import { ApplessOccupancyEventType } from 'utils/applessOccupancyUtils';
import _ from 'lodash';

// Props for `ExportSpaceCSV`
type ExportCSVProps = {
  entityName: string;
  spacesInBuilding?: SpaceProps[] | [];
  aggregatedUtilization?: AggregatedUsageResponse;
  buildingsData?: BuildingProps[];
  selectedBuildingUuid?: string | '';
  selectedAggregation?: Aggregation;
  startDate: Date;
  endDate: Date;
  selectedSpaceReportingType: SpaceReportingType;
  applessOccupancyLogs?: ApplessOccupancyLogsData[];
  selectedBuildingTimezone?: string;
};

// Type for Data to be exported
type ExportData = {
  spaceName?: string;
  time: string;
  uniqueOccupants?: number;
  event?: string;
};

export default function ExportSpaceCSV({
  entityName,
  spacesInBuilding,
  aggregatedUtilization,
  buildingsData,
  selectedBuildingUuid,
  selectedAggregation,
  startDate,
  endDate,
  selectedSpaceReportingType,
  applessOccupancyLogs,
  selectedBuildingTimezone
}: ExportCSVProps) {
  // Function to get the header label based on `selectedAggregation` type
  function getTimeHeaderLabel(aggregationType: Aggregation | undefined): string {
    switch (aggregationType) {
      case 'hourly':
        return 'hour_of_day';
      case 'daily':
        return 'day_of_week';
      case 'monthly':
        return 'month_of_year';
      case 'yearly':
        return 'year';
      default:
        return 'time';
    }
  }

  // Headers for the csv file
  const headers =
    selectedSpaceReportingType === SpaceReportingType.Occupants
      ? [
          { label: 'space_name', key: 'spaceName' },
          { label: getTimeHeaderLabel(selectedAggregation), key: 'time' },
          { label: 'unique_occupants', key: 'uniqueOccupants' }
        ]
      : [
          { label: 'date_and_time', key: 'time' },
          { label: 'location', key: 'spaceName' },
          { label: 'event', key: 'event' }
        ];

  // Function to generate data to be exported
  function getFilteredData() {
    const buildingTimezone = selectedBuildingTimezone || 'UTC';

    if (
      selectedSpaceReportingType === SpaceReportingType.Occupants &&
      spacesInBuilding &&
      aggregatedUtilization
    ) {
      const filteredData: ExportData[] = [];

      // Iterate over each object in `aggregatedUtilization` array
      aggregatedUtilization.forEach((usage) => {
        Object.keys(usage).forEach((uuid) => {
          const utilizationData = usage[uuid];
          const times = utilizationData.x;
          const uniqueOccupants = utilizationData.y;

          // Find the space in `spacesInBuilding` based on object's uuid
          const space = spacesInBuilding.find((spaceInBuilding) => spaceInBuilding.uuid === uuid);

          if (space) {
            const spaceName = space.name;

            times.forEach((time, index) => {
              filteredData.push({
                spaceName: spaceName,
                time: time,
                uniqueOccupants: parseFloat(uniqueOccupants[index].toFixed(3))
              });
            });
          }
        });
      });

      return filteredData;
    } else if (selectedSpaceReportingType === SpaceReportingType.People && applessOccupancyLogs) {
      return applessOccupancyLogs.map((log) => {
        let eventDescription = _.capitalize(log.event);
        if (log.event === ApplessOccupancyEventType.Set && log.eventValue !== undefined) {
          eventDescription += ` (${log.eventValue} people)`;
        }

        const zonedTime = utcToZonedTime(log.time, buildingTimezone);
        const formattedTime = format(zonedTime, 'yyyy-MM-dd HH:mm:ss XXX', {
          timeZone: buildingTimezone
        });

        return {
          spaceName: log.location,
          time: formattedTime,
          event: eventDescription
        };
      });
    }
  }

  const exportCsvData = getFilteredData();

  // Function to generate file name
  function generateFilename(): string {
    const selectedBuilding = buildingsData?.find(
      (building: BuildingProps) => building.uuid === selectedBuildingUuid
    );

    const formattedBuildingName = selectedBuilding
      ? selectedBuilding.name.toLowerCase().replace(/\s+/g, '_')
      : 'building';
    const formattedStartDate = formatDateToString(startDate);
    const formattedEndDate = formatDateToString(endDate);

    const prefix =
      selectedSpaceReportingType === SpaceReportingType.Occupants
        ? `occupants_${selectedAggregation}`
        : 'people';

    return `${prefix}_space_reporting_${formattedBuildingName}_${formattedStartDate}_${formattedEndDate}.csv`;
  }

  return (
    <>
      {exportCsvData && exportCsvData.length > 0 ? (
        <CSVLink
          data={exportCsvData}
          filename={generateFilename()}
          className={`secondary-btn default px-3 w-fit gap-2`}
          headers={headers}
          style={{ transition: 'background-color 0.3s ease' }}
          onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
            (e.currentTarget as HTMLAnchorElement).style.backgroundImage =
              'linear-gradient(to right, ' + colours.lightTeal + ', ' + colours.darkTeal + ')';
            (e.currentTarget as HTMLAnchorElement).style.color = 'white';
          }}
          onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => {
            (e.currentTarget as HTMLAnchorElement).style.backgroundImage = 'none';
            (e.currentTarget as HTMLAnchorElement).style.color = 'black';
          }}
        >
          {<ExportIcon />} {entityName}
        </CSVLink>
      ) : (
        <button
          className="secondary-btn default px-3 w-fit gap-2 disabled cursor-not-allowed"
          disabled={true}
        >
          {<ExportIcon />} {entityName}
        </button>
      )}
    </>
  );
}
